import { ReplicantError } from '../../Errors';
import type { FBChatbotMessage, SessionEndEventData } from '../../ReplicantChatbot';
import type { Replicant, ReplicantConfig } from '../../ReplicantConfig';
import { resolveEventHandlerAndWriteMessage } from '../../ReplicantEventHandlerMessages';
import SB from '../../SchemaBuilder';
import type { DelayedAction } from '../../api/ReplicantAPI';
import { trackSubscriptionUpdate } from '../../api/impl/PlatformChatbotAPI';
import type { PostAnalyticsFN } from '../../common/Analytics';
import type { ErrorReporter } from '../../common/ErrorReporter';
import { KV_MESSENGER_ID_PREFIX } from '../../common/ReplicantConstants';
import type { default as DB } from '../../db/DB';
import logger from '../../logger';
import { Immutable } from '../../utils/TypeUtils';
import { duration } from '../../utils/Utils';
import type {
    CreateEventHandlerAPIFn,
    GetReadOnlyEntryFn,
    PostNativeBridgeSecretFn,
    PushDocsToIndexFn,
    ResolveDelayedActionsFn,
} from '../ServerReplicant';
import ServerReplicantChatbot, { ON_GAME_END_NOTIFICATION_ID } from '../ServerReplicantChatbot';
import { ServerReplicantSystemScheduledActions } from '../ServerReplicantSystemScheduledActions';
import { withIndexDocGeneration } from '../utils/IndexUtils';

type FBBaseWebhookEvent = {
    sender: {
        id: string; // The PSID of the user
    };
    recipient?: { id: string };
    timestamp?: number;
};

type FBReferral = {
    ref: string;
    source: string;
    type: string;
    referer_uri?: string;
    is_guest_user?: string;
    ads_context_data?: {
        ad_title: string;
        photo_url: string;
        video_url: string;
        post_id: string;
    };
};

type FBGamePlayWebhookEvent = FBBaseWebhookEvent & {
    game_play: {
        game_id: string;
        player_id: string;
        context_type: 'SOLO' | 'THREAD' | 'GROUP';
        context_id: string;
        score: number;
        payload: string; // JSON-encoded
    };
};

type FBReferralWebhookEvent = FBBaseWebhookEvent & {
    referral: FBReferral;
};

type FBPostbackWebhookEvent = FBBaseWebhookEvent & {
    postback: {
        payload?: string; // JSON-encoded
        title?: string;
        referral?: FBReferral;
    };
};

export type FBWebhookEvent = FBGamePlayWebhookEvent | FBReferralWebhookEvent | FBPostbackWebhookEvent;

export default function createFBWebhookHandler<T extends Replicant>(opts: {
    config: ReplicantConfig<T>;
    dateNow: () => number;
    db: DB<T['state'], T['messages']>;
    chatbot: ServerReplicantChatbot<T>;
    createEventHandlerApi: CreateEventHandlerAPIFn<T>;
    fetchNativeBridgeSecret: (userId: string) => Promise<string>;
    getReadOnlyEntry: GetReadOnlyEntryFn<T>;
    messengerIdToPlayerId: (messengerId: string) => Promise<string | null>;
    postNativeBridgeSecret: PostNativeBridgeSecretFn;
    pushDocsToIndex: PushDocsToIndexFn;
    resolveDelayedActions: ResolveDelayedActionsFn;
    sendAnalyticsEvents: PostAnalyticsFN;
    systemScheduledActions: ServerReplicantSystemScheduledActions;
    errorReporter?: ErrorReporter;
}) {
    const handleGamePlayWebhook = async (webhookEvent: FBGamePlayWebhookEvent): Promise<void> => {
        const gamePlay = webhookEvent.game_play;
        let gamePlayPayload;
        if (gamePlay.payload) {
            try {
                gamePlayPayload = JSON.parse(gamePlay.payload);
            } catch (e) {
                gamePlayPayload = undefined;
            }
        }

        const eventData: SessionEndEventData = {
            playerId: gamePlay.player_id,
            messengerId: webhookEvent.sender.id,
            contextType: gamePlay.context_type,
            contextId: gamePlay.context_id,
            payload: gamePlayPayload,
            score: gamePlay.score,
        };

        // Fire fetching reverse mapping entry for PSID -> userId lookup in parallel to the other fetch requsts.
        const reverseMappingEntryPromise = opts.db.internalKvStore.fetch(
            KV_MESSENGER_ID_PREFIX + eventData.messengerId,
        );

        // Fetch, migrate and apply messages to user.
        const readonlyEntry = await opts.getReadOnlyEntry(eventData.playerId);

        if (!readonlyEntry) {
            // Report warning without throwing exception to prevent useless webhook retry:
            const error = new ReplicantError(
                'Chatbot webhook target user does not exist in Replicant. This can happen if the player subscribes to the platform chatbot before completing their first Replicant login.',
                'server_error',
                'chatbot_missing_player',
                'warning',
            );

            await opts.errorReporter?.captureException(error, { user: { id: eventData.playerId } });
            return;
        }

        const { entry, isNewUser } = readonlyEntry;

        if (!entry.chatbotMetainfo || entry.chatbotMetainfo.messengerId !== eventData.messengerId) {
            // Need to update the messenger id.
            entry.chatbotMetainfo = await opts.db.chatbot.setMessengerId(eventData.playerId, eventData.messengerId);
        }

        // Update reverse mapping if necessary.
        // Note: it's not done only when messengerId changes in chatbot metainfo, in order to account for players who
        // are already subscribed, but do not have the reverse lookup entry populated.
        const reverseMappingEntry = await reverseMappingEntryPromise;
        if (!reverseMappingEntry) {
            await opts.db.internalKvStore.write(KV_MESSENGER_ID_PREFIX + eventData.messengerId, eventData.playerId);
        }

        // Since we're receiving a chatbot event, this means we're subscribed; also reset session quota:
        entry.chatbotMetainfo = await opts.db.chatbot.markSubscribedAndResetMessagingSession(
            eventData.playerId,
            true,
            opts.dateNow(),
        );
        await opts.sendAnalyticsEvents([trackSubscriptionUpdate(eventData.playerId, true)]);

        const chatbot = opts.config.chatbot;
        const onGameEnd = chatbot?.events?.onGameEnd;

        if (chatbot && onGameEnd) {
            const payload = eventData.payload as any;
            const sessionId: string | undefined = payload?.sessionId ? payload.sessionId : undefined;
            const isGameFirstInSession = !sessionId || entry.chatbotMetainfo.lastSessionId !== sessionId;

            // FB invokes this webhook on each context switch as well as game end whereas we only want to invoke onGameEnd at game end.
            // Thus if we detect a context switch event schedule onGameEnd invocation with a delay, and otherwise invoke onGameEnd immediately.
            // See https://playco.atlassian.net/browse/CORE-1073 for details.

            // The eventId and eventType properties are set by GCInstant extensions, requires https://docs.dev.gc-internal.net/gcinstant/replicantExtensions/#usage.
            const contextSwitchPayloadSchema = SB.object({
                eventId: SB.string().optional(),
                eventType: SB.tuple(['CONTEXT_SWITCH']),
            }).additionalProperties();
            const isContextSwitchEvent = contextSwitchPayloadSchema.validate(eventData.payload) === null;

            if (isContextSwitchEvent) {
                // Read metainfo again consistently for the latest `cancelledOnGameEndEventIds` value:
                const metainfoMVCC = await opts.db.metainfoMVCC.fetchMetainfoMVCC(eventData.playerId, {
                    consistentRead: true,
                });

                // Ignore the context switch event if we've already mark it as cancelled.
                // Context switch events are cancelled in Replicant's afterContextSwitch hook which typically resolves before the FB webhook reaches Replicant.
                const { eventId } = eventData.payload as SB.ExtractType<typeof contextSwitchPayloadSchema>;
                const isCancelledEvent = eventId && metainfoMVCC.cancelledOnGameEndEventIds?.includes(eventId);
                if (isCancelledEvent) {
                    return;
                }

                await opts.systemScheduledActions.schedule.onGameEnd({
                    args: {
                        // Don't pass in eventData as is to avoid breaking scheduled action args validation whenever FB changes the event data:
                        eventData: {
                            messengerId: eventData.messengerId,
                            playerId: eventData.playerId,
                            contextId: eventData.contextId,
                            contextType: eventData.contextType,
                            payload: eventData.payload as Record<string, any>,
                            score: eventData.score,
                        },
                        isFirstInSession: isGameFirstInSession,
                    },
                    delayInMS: duration({ minutes: chatbot.opts.onGameEndTimeoutMinutes }),
                    notificationId: ON_GAME_END_NOTIFICATION_ID,
                    userData: entry,
                    userId: eventData.playerId,
                });

                return;
            }

            // The event was not caused by context switch so the user must've actually ended the game -
            // unschedule any previously scheduled delayed invocations and invoke onGameEnd immediately:
            await opts.systemScheduledActions.unschedule({
                notificationId: ON_GAME_END_NOTIFICATION_ID,
                userData: entry,
                userId: eventData.playerId,
            });

            const delayedActions: DelayedAction[] = [];

            const api = await opts.createEventHandlerApi({
                consistentFetchIds: [], // This does not come from the client, so there are no consistent fetch ids.
                delayedActions,
                entry,
                userId: eventData.playerId,
            });

            const eventHandler = async (): Promise<void> => {
                await onGameEnd(entry.state as Immutable<T['state']>, eventData, api, isGameFirstInSession);

                // validate the state after the handler has run
                opts.config.stateSchema.tryValidate(entry.state);

                // Execute any scheduled actions by the api.
                await opts.resolveDelayedActions(delayedActions);
            };

            const newIndexDoc = await withIndexDocGeneration(
                eventData.playerId,
                entry,
                opts.config,
                opts.dateNow,
                isNewUser,
                () =>
                    resolveEventHandlerAndWriteMessage({
                        db: opts.db,
                        entry,
                        event: { type: 'onGameEnd' },
                        eventHandler,
                        now: opts.dateNow(),
                        userId: eventData.playerId,
                    }),
            );

            if (newIndexDoc) {
                await opts.pushDocsToIndex({ [eventData.playerId]: newIndexDoc });
            }

            // Write back the current sessionId
            if (sessionId) {
                entry.chatbotMetainfo = await opts.db.chatbot.setSessionId(eventData.playerId, sessionId);
            }
        }
    };

    const handleReferralWebhook = async (webhookEvent: FBReferralWebhookEvent) => {
        // The `onWebhook` chatbot event handler can include custom handling for referral events: thus ignore referral events here if `onWebhook` is defined.
        const onWebhookIsConfigured = !!opts.config.chatbot?.events?.onWebhook;
        if (onWebhookIsConfigured) {
            return;
        }

        const referralCode = webhookEvent.referral.ref;
        const messengerId = webhookEvent.sender.id;

        try {
            const playerId = await opts.messengerIdToPlayerId(messengerId);

            if (!playerId) {
                throw new ReplicantError(
                    `No player found for PSID: ${messengerId} `,
                    'server_error',
                    'webhook_referral_player_not_found',
                    'fatal',
                );
            }

            const bridgeSecret = await opts.fetchNativeBridgeSecret(playerId);

            await opts.postNativeBridgeSecret({ bridgeSecret, playerId, referralCode });

            // Remove this hardcoded stuff from here.
            // TODO: make sure these are configurable.
            //
            if (!process.env.REFERRAL_WEBHOOK_URL) {
                throw new ReplicantError(
                    'No referral webhook url configured. Expected process.env.REFERRAL_WEBHOOK_URL to be set',
                    'server_error',
                    'webhook_referral_url_not_set',
                    'fatal',
                );
            }

            const url = process.env.REFERRAL_WEBHOOK_URL;

            const payload: FBChatbotMessage = {
                messaging_type: 'UPDATE',
                message: {
                    attachment: {
                        type: 'template',
                        payload: {
                            template_type: 'generic',
                            image_aspect_ratio: 'square',
                            elements: [
                                {
                                    title: 'Success',
                                    subtitle: 'Account successfully linked!',

                                    default_action: {
                                        type: 'web_url',
                                        url,
                                    },

                                    buttons: [
                                        {
                                            title: 'Play',
                                            type: 'web_url',
                                            url,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                },
            };

            const response = await opts.chatbot.sendRawChatbotMessage(messengerId, payload);

            if (response.statusCode !== 200) {
                const errorBody = response.body;

                await opts.errorReporter?.captureException(
                    new ReplicantError(
                        'Referral webhook callback failed to send chatbot message',
                        'server_error',
                        'chatbot_send_error',
                        'error',
                        {
                            receiverId: playerId,
                            template: JSON.stringify(payload),
                            errorBody,
                        },
                    ),
                );
            }

            logger.debug(response.body);
        } catch (e: any) {
            await opts.errorReporter?.captureException(e);

            const payload = {
                messaging_type: 'UPDATE',
                message: {
                    text: 'An error occurred trying to link your accounts. Please make sure that you are subscribed to the chatbot in the game and try again. If the error persists, please contact us at thuglife@chobolabs.com',
                },
            };

            logger.debug('ERROR: ' + e.message);

            const response = await opts.chatbot.sendRawChatbotMessage(messengerId, payload);
            logger.debug(response.body);
        }
    };

    const handlePostbackWebhook = async (webhookEvent: FBPostbackWebhookEvent) => {
        // The `onWebhook` chatbot event handler can include custom handling for postback events: thus ignore postback events here if `onWebhook` is defined.
        const onWebhookIsConfigured = !!opts.config.chatbot?.events?.onWebhook;
        if (onWebhookIsConfigured) {
            return;
        }

        let title: string | undefined;
        let subtitle: string | undefined;
        let imageUrl: string | undefined;
        let url: string | undefined;

        let cta = 'Play Now';

        logger.debug(webhookEvent.postback);

        const postbackPayload = webhookEvent.postback?.payload;

        if (postbackPayload) {
            const parsedPayload = JSON.parse(postbackPayload);

            url = parsedPayload.url;
            title = parsedPayload.title;
            subtitle = parsedPayload.subtitle;
            imageUrl = parsedPayload.image_url;
            cta = parsedPayload.cta || cta;
        }

        if (!title || !imageUrl || !url) {
            const missingPayloadParams = [];

            if (!title) missingPayloadParams.push('title');
            if (!imageUrl) missingPayloadParams.push('imageUrl');
            if (!url) missingPayloadParams.push('url');

            throw new ReplicantError(
                `Missing required postback webhook payload params: ${missingPayloadParams
                    .map((item) => `'${item}'`)
                    .join(', ')}`,
                'server_error',
                'webhook_postback_missing_params',
                'error',
            );
        }

        const messengerId = webhookEvent.sender.id;

        try {
            const payload: FBChatbotMessage = {
                messaging_type: 'UPDATE',
                message: {
                    attachment: {
                        type: 'template',
                        payload: {
                            template_type: 'generic',
                            image_aspect_ratio: 'square',
                            elements: [
                                {
                                    title,
                                    subtitle,
                                    image_url: imageUrl,

                                    default_action: {
                                        type: 'web_url',
                                        url,
                                    },

                                    buttons: [
                                        {
                                            title: cta,
                                            type: 'web_url',
                                            url,
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                },
            };

            const response = await opts.chatbot.sendRawChatbotMessage(messengerId, payload);

            if (response.statusCode !== 200) {
                const errorBody = response.body;
                let playerId: string | null = null;

                try {
                    playerId = await opts.messengerIdToPlayerId(messengerId);
                } catch {
                    // silently ignore trivial error
                }

                await opts.errorReporter?.captureException(
                    new ReplicantError(
                        'Postback webhook callback failed to send chatbot message',
                        'server_error',
                        'chatbot_send_error',
                        'error',
                        {
                            receiverId: playerId,
                            template: JSON.stringify(payload),
                            errorBody,
                        },
                    ),
                );
            }

            logger.debug(response.body);
        } catch (e: any) {
            await opts.errorReporter?.captureException(e);

            logger.debug('ERROR IN POSTBACK WEBHOOK: ' + e.message);
        }
    };

    return async (webhookEvents: FBWebhookEvent[]) => {
        // See event types in https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/
        for (const we of webhookEvents) {
            if ('game_play' in we) {
                await handleGamePlayWebhook(we);
            } else if ('referral' in we) {
                logger.info('*FACEBOOK_WEBHOOK_DEBUG* Referral webhook triggered');
                await handleReferralWebhook(we);
            } else if ('postback' in we) {
                logger.info('*FACEBOOK_WEBHOOK_DEBUG* Postback webhook triggered');
                await handlePostbackWebhook(we);
            }
        }
    };
}
